"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useCallback } from "react";
import { Upload, FileText, Loader2, CheckCircle, AlertCircle, XCircle, Clock, MapPin, Hash } from "lucide-react";
import { useTenant } from "@/lib/tenant";
import type { DealVerification } from "@/lib/deals/verification";
import { dealDocumentUrl } from "@/lib/deals/document-download/refs";
interface OkokontoUploadProps {
  dealId: string;
  verification: DealVerification | null;
  offeringDocumentUrl?: string | null;
  offeringDocumentName?: string | null;
  onUploadComplete?: (verification: DealVerification) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * OCR comparison result card shown after upload
 */
function OcrComparisonSummary(t0) {
  const $ = _c(12);
  const {
    verification
  } = t0;
  const locationDetails = verification.location_match_details as {
    source?: string;
    ocrKreis?: string | null;
    ocrBundesland?: string | null;
    offeringLandkreis?: string | null;
    offeringBundesland?: string | null;
    confidence?: number;
  } | null;
  const quantityDetails = verification.quantity_match_details as {
    source?: string;
    ocrTotalGuthaben?: number | null;
    offeringQuantity?: number | null;
    confidence?: number;
  } | null;
  if (locationDetails?.source !== "ocr" && quantityDetails?.source !== "ocr") {
    return null;
  }
  const confidence = Math.max(locationDetails?.confidence ?? 0, quantityDetails?.confidence ?? 0);
  const isLowConfidence = confidence < 0.5;
  if (isLowConfidence) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3"><div className="flex items-center gap-2 text-sm text-amber-700"><Clock className="w-4 h-4 flex-shrink-0" /><span>Automatische Prüfung nicht möglich — wird manuell geprüft</span></div></div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <p className="text-xs font-medium text-zinc-500 uppercase tracking-wide">Automatischer Abgleich</p>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== locationDetails || $[3] !== verification.location_match) {
    t2 = locationDetails?.source === "ocr" && <div className="flex items-start gap-2"><div className="mt-0.5">{verification.location_match === true ? <CheckCircle className="w-4 h-4 text-emerald-500" /> : verification.location_match === false ? <XCircle className="w-4 h-4 text-red-500" /> : <Clock className="w-4 h-4 text-zinc-400" />}</div><div className="flex-1 min-w-0"><div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-zinc-400" /><span className="text-sm text-zinc-700">Standort</span></div><p className="text-xs text-zinc-500 mt-0.5">Erkannt:{" "}{[locationDetails.ocrKreis, locationDetails.ocrBundesland].filter(Boolean).join(", ") || "\u2014"}</p><p className="text-xs text-zinc-500">Angebot:{" "}{[locationDetails.offeringLandkreis, locationDetails.offeringBundesland].filter(Boolean).join(", ") || "\u2014"}</p></div></div>;
    $[2] = locationDetails;
    $[3] = verification.location_match;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== quantityDetails || $[6] !== verification.quantity_match) {
    t3 = quantityDetails?.source === "ocr" && <div className="flex items-start gap-2"><div className="mt-0.5">{verification.quantity_match === true ? <CheckCircle className="w-4 h-4 text-emerald-500" /> : verification.quantity_match === false ? <XCircle className="w-4 h-4 text-red-500" /> : <Clock className="w-4 h-4 text-zinc-400" />}</div><div className="flex-1 min-w-0"><div className="flex items-center gap-1.5"><Hash className="w-3.5 h-3.5 text-zinc-400" /><span className="text-sm text-zinc-700">Punktzahl</span></div><p className="text-xs text-zinc-500 mt-0.5">Erkannt:{" "}{quantityDetails.ocrTotalGuthaben != null ? `${quantityDetails.ocrTotalGuthaben.toLocaleString("de-DE")} Ökopunkte` : "\u2014"}</p><p className="text-xs text-zinc-500">Angebot:{" "}{quantityDetails.offeringQuantity != null ? `${quantityDetails.offeringQuantity.toLocaleString("de-DE")} Ökopunkte` : "\u2014"}</p></div></div>;
    $[5] = quantityDetails;
    $[6] = verification.quantity_match;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <p className="text-xs text-zinc-400 pt-1">Wird vom Team geprüft</p>;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t2 || $[10] !== t3) {
    t5 = <div className="mt-3 rounded-lg border border-zinc-200 bg-zinc-50 p-3 space-y-2">{t1}{t2}{t3}{t4}</div>;
    $[9] = t2;
    $[10] = t3;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}

/**
 * OkokontoUpload - Component for sellers to upload Ökokonto documents
 */
export function OkokontoUpload({
  dealId,
  verification,
  offeringDocumentUrl,
  offeringDocumentName,
  onUploadComplete,
  disabled = false,
  className = ""
}: OkokontoUploadProps) {
  const tenant = useTenant();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const hasDocument = !!verification?.okokonto_document_url;
  const canUpload = !disabled && (!hasDocument || verification?.verification_status === "needs_info");
  const handleUpload = useCallback(async (file: File) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      setUploadError("Bitte laden Sie eine PDF- oder Bilddatei hoch.");
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setUploadError("Die Datei ist zu groß. Maximale Größe: 10 MB.");
      return;
    }
    setIsUploading(true);
    setUploadError(null);
    try {
      // Create form data
      const formData = new FormData();
      formData.append("file", file);

      // Upload to API
      const response = await fetch(`/api/deals/${dealId}/verification/upload`, {
        method: "POST",
        body: formData
      });
      if (!response.ok) {
        const error_0 = await response.json();
        throw new Error(error_0.message || "Upload fehlgeschlagen");
      }
      const data = await response.json();
      onUploadComplete?.(data.verification);
    } catch (error) {
      console.error("[OkokontoUpload] Upload error:", error);
      setUploadError(error instanceof Error ? error.message : "Upload fehlgeschlagen");
    } finally {
      setIsUploading(false);
    }
  }, [dealId, onUploadComplete]);
  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    if (!canUpload) return;
    const file_0 = e.dataTransfer.files[0];
    if (file_0) {
      handleUpload(file_0);
    }
  }, [canUpload, handleUpload]);
  const handleDragOver = useCallback((e_0: React.DragEvent<HTMLDivElement>) => {
    e_0.preventDefault();
    setDragActive(true);
  }, []);
  const handleDragLeave = useCallback((e_1: React.DragEvent<HTMLDivElement>) => {
    e_1.preventDefault();
    setDragActive(false);
  }, []);
  const handleFileInput = useCallback((e_2: React.ChangeEvent<HTMLInputElement>) => {
    const file_1 = e_2.target.files?.[0];
    if (file_1) {
      handleUpload(file_1);
    }
  }, [handleUpload]);

  // Show existing document with OCR comparison summary
  if (hasDocument && verification?.verification_status !== "needs_info") {
    return <div className={className}>
        <div className="rounded-lg border border-zinc-200 bg-white p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
              <FileText className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-zinc-900 truncate">
                {verification?.okokonto_document_name || "Ökokonto-Dokument"}
              </p>
              <p className="text-sm text-zinc-500">
                Hochgeladen am{" "}
                {verification?.uploaded_at ? new Date(verification.uploaded_at).toLocaleDateString("de-DE") : "—"}
              </p>
            </div>
            <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
          </div>

          {/* OCR comparison results */}
          {verification && <OcrComparisonSummary verification={verification} />}
        </div>
      </div>;
  }
  return <div className={className}>
      {/* Show pre-funnel document from offering if no deal-verification document yet */}
      {!hasDocument && offeringDocumentUrl && <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-blue-800">
                Bereits hochgeladen bei Angebotserstellung
              </p>
              <p className="text-sm text-blue-600 truncate">
                {offeringDocumentName || "Ökokonto-Dokument"}
              </p>
            </div>
            <a href={dealDocumentUrl(dealId, "offering-okokonto")} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-blue-700 hover:text-blue-900 underline flex-shrink-0">
              Ansehen
            </a>
          </div>
        </div>}
      <div onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave} className={`
          relative rounded-lg border-2 border-dashed transition-colors
          ${dragActive ? "border-primary bg-primary/5" : "border-zinc-300 bg-zinc-50"}
          ${canUpload ? "cursor-pointer hover:border-zinc-400" : "cursor-not-allowed opacity-60"}
        `} style={dragActive ? {
      borderColor: tenant.theme.primary
    } : undefined}>
        <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileInput} disabled={!canUpload || isUploading} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed" />

        <div className="p-6 text-center">
          {isUploading ? <>
              <Loader2 className="w-10 h-10 mx-auto text-zinc-400 animate-spin" />
              <p className="mt-3 text-sm font-medium text-zinc-600">Dokument wird analysiert...</p>
              <p className="mt-1 text-xs text-zinc-400">Upload und automatischer Abgleich</p>
            </> : <>
              <Upload className="w-10 h-10 mx-auto text-zinc-400" />
              <p className="mt-3 text-sm font-medium text-zinc-700">
                {verification?.verification_status === "needs_info" ? "Neues Dokument hochladen" : "Ökokonto-Dokument hochladen"}
              </p>
              <p className="mt-1 text-xs text-zinc-500">PDF, JPG oder PNG, max. 10 MB</p>
              <p className="mt-2 text-xs text-zinc-400">
                Ziehen Sie die Datei hierher oder klicken Sie zum Auswählen
              </p>
            </>}
        </div>
      </div>

      {uploadError && <div className="mt-3 flex items-center gap-2 text-sm text-red-600">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{uploadError}</span>
        </div>}
    </div>;
}